import { createTheme, ThemeOptions } from '@mui/material/styles';
import { IExtendedThemeOptions, IExtendedTheme } from './ExtendedThemeOptions';
import { leastSquaresFit } from './theme.util';
 
 export const extranalColor= {
  blue: '#3191D6',
  lightBlue: '#EBF4FF',
  green: '#217A7B',
  lightGreen: '#E6FEFA',
  warning: '#FFB74D',
  lightWarning: '#FFF3E0',
  error: '#F44336',
  lightError: '#FFEBEE',
  purple: '#952BB3',
  lightPurple: '#F3E5F5',
  aqua: "#35B6AE"
}
export const themeConfig: IExtendedThemeOptions = {
  spacing: (factor: number) => [0, 4, 8, 16, 32, 64][factor],
  breakpoints: {
    values: {
      xs: 0,
      sm: 480,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          display: 'flex',
          WebkitFontSmoothing: 'auto',
          minHeight: '100%',
        },
        body: {
          overflowX: 'hidden',
          overflowY: 'auto !important',
          flexGrow: 1,
          minHeight: '100%',
          fontFamily: 'Poppins, sans-serif',
          backgroundColor: '#F6F7FB !important',
          fontWeight: 400,
        },
        '*::-webkit-scrollbar': {
          width: 6,
        },
        '*::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 4,
        },
        '*::-webkit-scrollbar-thumb': {
          background: '#dadce0',
          borderRadius: 4,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: '#a0a0a0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          padding: '12px 20px',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        containedPrimary: {
          backgroundColor: '#14192C',
          '&:hover': {
            backgroundColor: '#2A2F42',
          },
        },
        outlined: {
          borderColor: '#E0E0E0',
          '&:hover': {
            borderColor: '#BDBDBD',
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E0E0E0',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#BDBDBD',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#952BB3',
            borderWidth: '1px',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#757575',
          '&.Mui-focused': {
            color: '#952BB3',
          },
          fontSize: '0.65rem',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
          overflow: 'visible',
          maxWidth: 'none',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.65)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            color: '#757575',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
              borderColor: '#BDBDBD',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#952BB3',
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(149, 43, 179, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(149, 43, 179, 0.12)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: '#952BB3',
            height: 3,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            color: '#952BB3',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: '#E0E0E0',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#BDBDBD',
          '&.Mui-checked': {
            color: '#952BB3',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: 16,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#757575',
          '&.Mui-focused': {
            color: '#952BB3',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          fontSize: '0.75rem',
        },
      },
    },
  },
  palette: {
    // Brand Primary colors in 10% shades — dark to light
    primary: {
      main: '#662075',
      900: '#4a1654',
      800: '#551b61',
      700: '#5f1f6d',
      600: '#662075',
      500: '#732886',
      400: '#952BB3',
      300: '#a15aba',
      200: '#b874d4',
      100: '#d08dee',
      light: '#e8d4f5',
      dark: '#3f1249',
      contrastText: '#faf3ff',
    },
    // Brand Secondary colors in 10% shades — dark to light
    secondary: {
      main: '#F4B942',
      900: '#b6892d',
      800: '#c59534',
      700: '#d3a13a',
      600: '#e2ad41',
      500: '#F4B942',
      400: '#f6c75e',
      300: '#f8d57a',
      200: '#fae396',
      100: '#fcf1b3',
      light: '#fff6dc',
      dark: '#a7751f',
      contrastText: '#fff',
    },
    error: {
      main: '#ED4B55',
      900: '#B51727',
      800: '#C4202F',
      700: '#D52935',
      600: '#E73A40',
      500: '#ED4B55',
      400: '#EF6A72',
      300: '#F18991',
      200: '#F5ADB3',
      100: '#F9D1D4',
      light: '#FEF0F1',
      contrastText: '#fff',
    },
    success: {
      main: '#4bb543',
      900: '#0e6410',
      800: '#298324',
      700: '#35942f',
      600: '#41a63a',
      500: '#4bb543',
      400: '#66c060',
      300: '#82cc7d',
      200: '#a6d9a3',
      100: '#c9e8c7',
      light: '#e8f6e8',
      contrastText: '#fff',
    },
    warning: {
      main: '#FF9800',
      900: '#964B00',
      800: '#B25900',
      700: '#CC6A00',
      600: '#E67700',
      500: '#FF9800',
      400: '#FFA726',
      300: '#FFB74D',
      200: '#FFCC80',
      100: '#FFE0B2',
      light: '#FFF3E0',
      contrastText: '#fff',
    },
    common: {
      black: '#000',
      white: '#fff',
      blue: '#4399E1',
      blueLight: '#EBF8FF',
    },
    info: {
      main: '#4A6CFA',
      500: '#4A6CFA',
      400: '#6680FF',
      300: '#8096FF',
      200: '#99ADFF',
      100: '#B3C5FF',
      light: '#E6EFFF',
      contrastText: '#fff',
    },
    background: {
      paper: '#fff',
      default: '#F6F7FB',
      light: '#F6F7FB',
    },
    text: {
      primary: '#313131',
      secondary: 'rgba(0, 0, 0, 0.54)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    // fontFamily: ['MulishRegular', 'sans-serif', 'Roboto'].join(','),
    fontFamily: ['Poppins', 'sans-serif'].join(','),
    h1: {
      fontSize: leastSquaresFit({ 0: 25, 480: 24, 960: 40 }),
      fontWeight: 600,
    },
    h2: {
      fontSize: leastSquaresFit({ 0: 20, 480: 26, 960: 36 }),
      fontWeight: 600,
    },
    h3: {
      fontSize: leastSquaresFit({ 0: 18, 480: 20, 960: 30 }),
      fontWeight: 600,
    },
    h4: {
      fontSize: leastSquaresFit({ 0: 16, 480: 18, 960: 24 }),
      fontWeight: 600,
    },
    h5: {
      fontSize: leastSquaresFit({ 0: 14, 480: 16, 960: 18 }),
      fontWeight: 600,
    },
    body1: {
      fontWeight: 500,
      fontSize: leastSquaresFit({ 0: 14, 480: 14, 960: 16 }),
    },
    body2: {
      fontWeight: 400,
      fontSize: leastSquaresFit({ 0: 12, 480: 12, 960: 14 }),
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    fontSizes: {
      xs: leastSquaresFit({ 0: 10, 480: 10, 960: 12 }),
      sm: leastSquaresFit({ 0: 12, 480: 12, 960: 14 }),
      md: leastSquaresFit({ 0: 14, 480: 14, 960: 16 }),
      lg: leastSquaresFit({ 0: 14, 480: 16, 960: 18 }),
      xlg: leastSquaresFit({ 0: 16, 480: 18, 960: 20 }),
      xxlg: leastSquaresFit({ 0: 20, 480: 24, 960: 30 }),
      link: leastSquaresFit({ 0: 12, 480: 12, 960: 12 }),
    },
  },
};

export default createTheme(themeConfig as ThemeOptions) as IExtendedTheme;